// @LTG-ID: F-012
// @STATUS: BUILT
// @HERMETIC: false  // consumes ExternalCTA, DIAG_Q, DIAG_PILLARS, diagnosticVerdict
// @DOCS: 08_FEATURES.md#F-012, 10_DECISIONS.md#ADR-012 (D4), source Landing Page.html lines 2560-2670
// @CHANGED: 2026-02 session_003 (CHG-0041)
// @OWNER: web
//
// F-012 — 4-question exit-readiness diagnostic with SVG donut ring + verdict.
// Ring geometry r=88, C=2πR ≈ 553. Per ADR-012 D4, the dash-offset is driven
// via requestAnimationFrame instead of CSS transition: stroke-dashoffset, so
// React state updates can't race a CSS transition mid-flight on rapid clicks.
// Per ORCH-0031 #5, state resets on re-open — no sessionStorage persistence.

import { useEffect, useRef, useState } from "react";
import {
    DIAG_Q,
    DIAG_PILLARS,
    diagnosticVerdict,
} from "../../data/diagnostic";
import BubblePeriod from "../brand/BubblePeriod";
import ExternalCTA from "../../lib/external-cta";
import "./diagnostic.css";

const R = 88;
const C = 2 * Math.PI * R;
const ANIM_MS = 1000;

// Hook: animate a numeric value from current → target over durationMs using RAF.
// Idempotent against rapid target changes (cancels in-flight RAF before starting).
// ADR-012 D4 deviation: replaces source's CSS `transition: stroke-dashoffset 1s ease`.
function useAnimatedValue(target, durationMs) {
    const [value, setValue] = useState(target);
    const fromRef = useRef(target);
    const rafRef = useRef(0);
    useEffect(() => {
        const from = fromRef.current;
        const to = target;
        if (from === to) return undefined;
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - start) / durationMs);
            // ease-out cubic, visually similar to CSS `transition: … ease`
            const eased = 1 - Math.pow(1 - t, 3);
            const next = from + (to - from) * eased;
            setValue(next);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                fromRef.current = to;
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, durationMs]);
    return value;
}

export function Diagnostic() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState([]);
    const done = step >= DIAG_Q.length;

    function answer(score) {
        setAnswers((a) => [...a, score]);
        setStep((s) => s + 1);
    }

    function restart() {
        setAnswers([]);
        setStep(0);
    }

    const total = answers.reduce((a, b) => a + b, 0);
    const max = DIAG_Q.length * 25;
    const pct = Math.round((total / max) * 100);
    const { verdict, note } = diagnosticVerdict(pct);

    // Animate the ring percentage 0 → pct via RAF (ADR-012 D4)
    const animatedPct = useAnimatedValue(done ? pct : 0, ANIM_MS);
    const offset = C * (1 - animatedPct / 100);

    return (
        <section
            className="ltg-diag-section"
            id="exit"
            data-testid="diagnostic-section"
        >
            <div className="ltg-narrow">
                <div className="ltg-block-head">
                    <span
                        className="ltg-eyebrow"
                        data-testid="diagnostic-eyebrow"
                    >
                        Exit-readiness · 90 seconds
                    </span>
                    <h2 data-testid="diagnostic-h2">
                        How close to <em>sellable</em> are you, really?
                    </h2>
                </div>

                <div className="ltg-diag-wrap">
                    <div
                        className="ltg-diag-card"
                        data-testid="diagnostic-card"
                    >
                        {!done ? (
                            <>
                                <div
                                    className="ltg-diag__progress"
                                    role="progressbar"
                                    aria-valuemin={0}
                                    aria-valuemax={DIAG_Q.length}
                                    aria-valuenow={step}
                                    data-testid="diagnostic-progress"
                                >
                                    {DIAG_Q.map((_, i) => (
                                        <span
                                            key={i}
                                            className={
                                                i < step
                                                    ? "ltg-diag__progress-seg ltg-diag__progress-seg--done"
                                                    : i === step
                                                      ? "ltg-diag__progress-seg ltg-diag__progress-seg--active"
                                                      : "ltg-diag__progress-seg"
                                            }
                                        />
                                    ))}
                                </div>
                                <span
                                    className="ltg-diag__q-num"
                                    data-testid="diagnostic-q-num"
                                >
                                    Question {step + 1} of {DIAG_Q.length}
                                </span>
                                <h3
                                    className="ltg-diag__q"
                                    data-testid="diagnostic-q"
                                >
                                    {DIAG_Q[step].q}
                                </h3>
                                <div className="ltg-diag__options">
                                    {DIAG_Q[step].opts.map((o, i) => (
                                        <button
                                            type="button"
                                            key={i}
                                            className="ltg-diag__opt"
                                            onClick={() => answer(o.score)}
                                            data-testid={`diagnostic-opt-${step}-${i}`}
                                        >
                                            {o.text}
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div
                                className="ltg-diag__result"
                                data-testid="diagnostic-result"
                            >
                                <span className="ltg-diag__q-num">
                                    Your readiness
                                </span>
                                <div
                                    className="ltg-diag__ring"
                                    data-testid="diagnostic-ring"
                                >
                                    <svg
                                        width="200"
                                        height="200"
                                        viewBox="0 0 200 200"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            className="ltg-diag__ring-bg"
                                            cx="100"
                                            cy="100"
                                            r={R}
                                            fill="none"
                                            strokeWidth="10"
                                        />
                                        <circle
                                            className="ltg-diag__ring-fg"
                                            cx="100"
                                            cy="100"
                                            r={R}
                                            fill="none"
                                            strokeWidth="10"
                                            strokeDasharray={C}
                                            strokeDashoffset={offset}
                                        />
                                    </svg>
                                    <div
                                        className="ltg-diag__ring-num"
                                        data-testid="diagnostic-ring-num"
                                    >
                                        {Math.round(animatedPct)}
                                    </div>
                                </div>
                                <h3
                                    className="ltg-diag__verdict"
                                    data-testid="diagnostic-verdict"
                                >
                                    {verdict}
                                    <BubblePeriod />
                                </h3>
                                <p className="ltg-diag__verdict-note">{note}</p>
                                <div className="ltg-diag__result-ctas">
                                    <ExternalCTA
                                        ctaId="diagnostic-result-order"
                                        className="ltg-btn ltg-btn-primary ltg-diag__result-cta"
                                        data-testid="diagnostic-order-cta"
                                    >
                                        Get Your Valuation{" "}
                                        <span
                                            className="ltg-arrow"
                                            aria-hidden="true"
                                        >
                                            →
                                        </span>
                                    </ExternalCTA>
                                    <button
                                        type="button"
                                        className="ltg-btn ltg-btn-ghost ltg-diag__result-reset"
                                        onClick={restart}
                                        data-testid="diagnostic-reset"
                                    >
                                        Reset the diagnostic
                                    </button>
                                    <span className="ltg-diag__disclaimer">
                                        Indicative valuation. Not a formal
                                        appraisal under SSVS No. 1.
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="ltg-diag-aside">
                        <span className="ltg-eyebrow">Why these four</span>
                        <h3>
                            Four questions, four levers worth ~70% of your
                            enterprise value.
                        </h3>
                        <p>
                            The full Exit Readiness Index runs 32 questions
                            across the ten levers. These four are the ones
                            we've watched move LOIs by seven figures, more than
                            once.
                        </p>
                        <div
                            className="ltg-diag__pillars"
                            data-testid="diagnostic-pillars"
                        >
                            {DIAG_PILLARS.map((p) => (
                                <div
                                    className="ltg-diag__pillar"
                                    key={p.num + p.title}
                                >
                                    <span className="ltg-diag__pillar-num">
                                        {p.num}
                                    </span>
                                    <span className="ltg-diag__pillar-text">
                                        <strong>{p.title}</strong>
                                        {p.text}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Diagnostic;
